import { useEffect, useRef, useState } from "react";
import { BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Field } from "@/components/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

export const ReminderJobButton = ({ variant = "outline" }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reminders/run");
      toast.success(`Reminder job: ${data.reminders} reminders · ${data.past_dues} past-due notices`, {
        description: `${data.loans_checked} loans checked · ${data.sent} sent · ${data.failed} failed · ${data.skipped} already sent`,
      });
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setBusy(false);
    }
  };
  return <Button variant={variant} onClick={run} disabled={busy} data-testid="run-reminder-job-button">{busy ? <Loader2 className="animate-spin" /> : <BellRing />} Run reminder job</Button>;
};

const Toggle = ({ id, label, hint, checked, onChange }) => (
  <label htmlFor={id} className="flex items-start justify-between gap-4 rounded-md border p-4">
    <span><span className="block text-sm font-semibold">{label}</span><span className="text-xs text-muted-foreground">{hint}</span></span>
    <Switch id={id} checked={checked} onCheckedChange={onChange} data-testid={`settings-${id}-switch`} />
  </label>
);

export const ReminderSettings = () => {
  const [f, setF] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const requested = useRef(false);
  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    api.get("/settings").then((r) => setF({ reminders_enabled: r.data.reminders_enabled, past_due_enabled: r.data.past_due_enabled,
      reminder_days_before: String(r.data.reminder_days_before), due_link_enabled: r.data.due_link_enabled }));
  }, []);
  if (!f) return null;

  const save = async (e) => {
    e.preventDefault();
    const d = Number(f.reminder_days_before);
    if (!Number.isInteger(d) || d < 1 || d > 14) { setErr("Days before due must be between 1 and 14"); return; }
    setBusy(true); setErr("");
    try {
      await api.put("/settings", { ...f, reminder_days_before: d });
      toast.success("Reminder settings saved");
    } catch (ex) {
      const { message, fields } = apiError(ex);
      setErr(fields.reminder_days_before || message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} noValidate className="fade-up mb-10 rounded-lg border bg-card p-6 sm:p-8" data-testid="reminder-settings-form">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Payment reminders</h3>
          <p className="mt-1 text-sm text-muted-foreground">Friendly emails before each due date and a past-due notice after the grace period. At most one of each per installment.</p>
        </div>
        <ReminderJobButton />
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <Toggle id="reminders-enabled" label="Upcoming reminders" hint="Email before each due date" checked={f.reminders_enabled} onChange={(v) => setF({ ...f, reminders_enabled: v })} />
        <Field label="Days before due" name="reminder_days_before" error={err} hint="1–14">
          <Input id="reminder_days_before" inputMode="numeric" value={f.reminder_days_before} onChange={(e) => setF({ ...f, reminder_days_before: e.target.value })}
            className={`h-10 bg-card ${err ? "border-destructive" : ""}`} data-testid="settings-reminder-days-input" />
        </Field>
        <Toggle id="past-due-enabled" label="Past-due notices" hint="Email once the grace period ends" checked={f.past_due_enabled} onChange={(v) => setF({ ...f, past_due_enabled: v })} />
        <Toggle id="due-link-enabled" label="Due-date pay link" hint="“Your payment is due today” email with a secure Pay now link" checked={f.due_link_enabled} onChange={(v) => setF({ ...f, due_link_enabled: v })} />
      </div>
      <div className="mt-6 flex justify-end">
        <Button type="submit" disabled={busy} data-testid="reminder-settings-save-button">{busy && <Loader2 className="animate-spin" />} Save reminder settings</Button>
      </div>
    </form>
  );
};
